/**
 * API Client Utility
 *
 * Centralized API request handling with:
 * - Automatic JWT authentication headers
 * - Automatic token refresh on 401 errors
 * - Consistent error handling
 * - Type-safe response parsing
 */

import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { API_TIMEOUT_MS } from '@/config/api.config';
import { refreshAccessToken, ensureValidToken } from './token-refresh';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/** HTTP methods that are safe to blindly replay after a 401 token refresh. */
const SAFE_RETRY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  timestamp?: string;
}

interface ApiError extends Error {
  code: string;
  statusCode: number;
  /**
   * The parsed response body of a failed request. Carried so callers can read
   * fields an error otherwise discards — e.g. the authoritative `diceValue`/
   * `turnEpoch` a `409 ALREADY_ROLLED`/`STALE_TURN` echoes for silent reconcile
   * (Story 04-04). Absent on errors thrown before a body is parsed (timeout/401).
   */
  body?: unknown;
}

/**
 * Request options for `apiRequest` and friends.
 *
 * `idempotent` is an **opt-in** flag: a mutating request (`POST`/`PUT`/`PATCH`/
 * `DELETE`) is never replayed after a 401 token refresh unless the caller
 * explicitly marks it idempotent (e.g. `roll-dice`, which carries a stable
 * `rollId`). This keeps the default "do not replay" — an opt-out default would
 * silently reintroduce double-submission bugs for every future endpoint.
 */
export interface ApiRequestOptions extends RequestInit {
  idempotent?: boolean;
}

/** Thrown when a request is aborted because it exceeded `API_TIMEOUT_MS`. */
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Wraps `fetch` with an `AbortController` + timeout so a request can never
 * hang indefinitely and resolve late against stale state (D17).
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (isAbortError(error)) {
      throw new TimeoutError(`Request timed out after ${String(timeoutMs)}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Whether a request may be replayed after a 401-triggered token refresh:
 * safe methods (GET/HEAD/OPTIONS) always may; mutating methods only when the
 * caller explicitly opted in via `idempotent: true`.
 */
function canReplayAfterRefresh(options: ApiRequestOptions): boolean {
  const method = (options.method ?? 'GET').toUpperCase();
  return SAFE_RETRY_METHODS.has(method) || options.idempotent === true;
}

/**
 * Make an authenticated API request with automatic token refresh
 *
 * @param endpoint - API endpoint (relative to API_URL, e.g., '/rooms' or '/rooms/join')
 * @param options - Fetch options (method, body, etc.)
 * @param isRetry - Internal flag to prevent infinite retry loops
 * @returns Promise with typed response data
 * @throws ApiError with code and statusCode for specific error handling
 */
async function apiRequestInternal<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
  isRetry = false
): Promise<T> {
  // Ensure token is valid before making request (handles returning from inactivity)
  if (!isRetry) {
    await ensureValidToken();
  }

  const url = `${API_URL}${endpoint}`;

  // Get access token from auth store
  const accessToken = useAuthStore.getState().accessToken;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  // Add JWT token to Authorization header
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetchWithTimeout(
    url,
    {
      ...options,
      headers,
      credentials: 'include'
    },
    API_TIMEOUT_MS
  );

  // Handle 403 responses that may have empty body (silent failures)
  if (response.status === 403) {
    const error = new Error('Forbidden') as ApiError;
    error.code = 'FORBIDDEN';
    error.statusCode = 403;
    throw error;
  }

  // Handle 401 UNAUTHORIZED - attempt token refresh
  if (response.status === 401) {
    // Don't retry if this is already a retry attempt
    if (isRetry) {
      const error = new Error('Unauthorized - Token refresh failed') as ApiError;
      error.code = 'UNAUTHORIZED';
      error.statusCode = 401;
      throw error;
    }

    // Opt-in, not opt-out: a non-idempotent mutating request is never
    // replayed after a 401 — surface it to the caller instead (D3).
    if (!canReplayAfterRefresh(options)) {
      const error = new Error('Unauthorized - request not retried (non-idempotent)') as ApiError;
      error.code = 'UNAUTHORIZED';
      error.statusCode = 401;
      throw error;
    }

    try {
      // Refresh token (handles concurrent requests internally)
      await refreshAccessToken();

      // Retry the original request verbatim, so any idempotency key
      // (e.g. rollId) rides along unchanged on the replay.
      return await apiRequestInternal<T>(endpoint, options, true);
    } catch {
      // Refresh failed, throw unauthorized error
      const error = new Error('Unauthorized - Invalid or expired token') as ApiError;
      error.code = 'UNAUTHORIZED';
      error.statusCode = 401;
      throw error;
    }
  }

  const result = (await response.json()) as ApiResponse<T>;

  if (!response.ok || !result.success) {
    const error = new Error(result.error?.message ?? 'Request failed') as ApiError;
    error.code = result.error?.code ?? 'UNKNOWN_ERROR';
    error.statusCode = response.status;
    // Preserve the parsed body so callers can reconcile from fields the error
    // shape omits (e.g. a 409's authoritative dice payload — Story 04-04).
    error.body = result;
    throw error;
  }

  if (!result.data) {
    throw new Error('No data returned from API');
  }

  return result.data;
}

/**
 * Make an authenticated API request
 * Public wrapper that starts with isRetry = false
 */
export async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  return apiRequestInternal<T>(endpoint, options, false);
}

/**
 * Make a GET request
 */
export async function get<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

/** Extra per-request options for mutating helpers (post/put/patch/del). */
export interface MutationRequestOptions {
  headers?: Record<string, string>;
  /** Opt-in: replay this request after a 401 refresh (see `ApiRequestOptions`). */
  idempotent?: boolean;
}

/**
 * Make a POST request
 * @param endpoint - API endpoint
 * @param body - Request body
 * @param options - Optional extra headers and/or the idempotent opt-in flag
 */
export async function post<T>(
  endpoint: string,
  body: unknown,
  options?: MutationRequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: options?.headers,
    idempotent: options?.idempotent
  });
}

/**
 * Make a DELETE request
 */
export async function del<T>(endpoint: string, options?: MutationRequestOptions): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'DELETE',
    headers: options?.headers,
    idempotent: options?.idempotent
  });
}

/**
 * Make a PUT request
 */
export async function put<T>(
  endpoint: string,
  body: unknown,
  options?: MutationRequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: options?.headers,
    idempotent: options?.idempotent
  });
}

/**
 * Make a PATCH request
 */
export async function patch<T>(
  endpoint: string,
  body: unknown,
  options?: MutationRequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: options?.headers,
    idempotent: options?.idempotent
  });
}
